import React, { useState, useEffect, useCallback, useId } from 'react';
import { TableSkeleton } from '../Skeleton';
import { AuditLog } from '../../types';
import { auditService, IntegrityResponse } from '../../services/auditService';
import { RefreshCw, ShieldCheck, ShieldAlert, Filter } from 'lucide-react';
import { ADMIN_TAB_FILL, ADMIN_LIST_FILL } from './adminLayout';

const ACTION_LABELS: Record<string, string> = {
  message_view: '메시지 열람',
  channel_audit: '채널 감사',
  user_audit: '사용자 감사',
  export_data: '데이터 내보내기',
  role_grant: '역할 부여',
  role_revoke: '역할 회수',
  channel_create_public: '공개 채널 생성',
  audit_log_view: '감사 로그 조회',
  upload_policy_update: '첨부 정책 변경',
  blocked_extension_add: '차단 확장자 추가',
  blocked_extension_remove: '차단 확장자 해제',
  attachment_purge: '첨부 만료 삭제',
  login_approval_grant: '로그인 승인',
  login_approval_revoke: '로그인 승인 해제',
  login_approval_policy_update: '로그인 승인제 변경',
  login_denied: '로그인 거부',
  remote_support_start: '원격지원 시작',
  remote_support_control_grant: '원격 제어 승인',
  remote_support_control_revoke: '원격 제어 종료',
  remote_support_end: '원격지원 종료',
  remote_support_denied: '원격지원 거절',
  remote_support_policy_update: '원격지원 정책 변경',
  remote_support_file_transfer: '원격지원 파일 전송',
  install_allowlist_update: '설치 허용 IP 변경',
  device_request: '기기 승인 요청',
  device_approve: '기기 승인',
  device_reject: '기기 요청 거절',
  device_revoke: '기기 승인 해제',
  device_confirm: '기기 본인 확인',
  device_policy_update: '기기 신뢰 정책 변경',
  device_denied: '차단 기기 로그인 시도',
};

export const AuditLogViewer: React.FC = () => {
  const fid = useId();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [integrity, setIntegrity] = useState<IntegrityResponse | null>(null);
  const [checkingIntegrity, setCheckingIntegrity] = useState(false);

  // Filters
  const [actionTypeFilter, setActionTypeFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const result = await auditService.getLogs({
        actionType: actionTypeFilter || undefined,
        limit: 50,
      });
      setLogs(result.logs);
      setTotal(result.total);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
    } finally {
      setLoading(false);
    }
  }, [actionTypeFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleIntegrityCheck = async () => {
    setCheckingIntegrity(true);
    try {
      const result = await auditService.verifyIntegrity();
      setIntegrity(result);
    } catch (err) {
      console.error('Integrity check failed:', err);
    } finally {
      setCheckingIntegrity(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('ko-KR', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  };

  return (
    <div className={ADMIN_TAB_FILL}>
      {/* Actions bar */}
      {/* 좁은 화면에선 버튼 그룹이 줄바꿈되도록 (shrink로 한글이 세로로 쪼개지는 것 방지) */}
      <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
        <span className="text-sm text-slate-500">
          총 {total}건의 감사 로그
        </span>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
              showFilters ? 'bg-brand-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            <Filter size={14} />
            필터
          </button>
          <button
            onClick={handleIntegrityCheck}
            disabled={checkingIntegrity}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            {checkingIntegrity ? (
              <RefreshCw size={14} className="animate-spin" />
            ) : (
              <ShieldCheck size={14} />
            )}
            무결성 검증
          </button>
          <button
            onClick={loadLogs}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            새로고침
          </button>
        </div>
      </div>

      {/* Integrity result */}
      {integrity && (
        <div className={`mb-4 px-4 py-3 rounded-xl border text-sm flex items-center gap-3 ${
          integrity.valid
            ? 'bg-green-50 border-green-200 text-green-700'
            : 'bg-brand-50 border-brand-200 text-brand-700'
        }`}>
          {integrity.valid ? (
            <>
              <ShieldCheck size={18} />
              <span>무결성 검증 통과 - {integrity.totalChecked}건의 로그 확인 완료</span>
            </>
          ) : (
            <>
              <ShieldAlert size={18} />
              <span>
                무결성 검증 실패 - {integrity.totalChecked}건 중 ID #{integrity.firstInvalidId}에서 변조 감지
              </span>
            </>
          )}
        </div>
      )}

      {/* Filters */}
      {showFilters && (
        <div className="mb-4 p-3 bg-slate-50 rounded-xl border border-slate-200">
          <div className="flex items-center gap-3">
            <label htmlFor={`${fid}-action`} className="text-xs text-slate-500">액션 유형:</label>
            <select id={`${fid}-action`}
              value={actionTypeFilter}
              onChange={(e) => setActionTypeFilter(e.target.value)}
              className="px-2 py-1 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-400"
            >
              <option value="">전체</option>
              {Object.entries(ACTION_LABELS).map(([key, label]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Log list */}
      {loading && logs.length === 0 ? (
        <TableSkeleton rows={5} cols={4} />
      ) : logs.length === 0 ? (
        <div className="text-center py-12 text-slate-400">감사 로그가 없습니다.</div>
      ) : (
        <div className={`space-y-1.5 ${ADMIN_LIST_FILL}`}>
          {logs.map((log) => (
            <div
              key={log.id}
              className="p-3 bg-white border border-slate-200 rounded-lg text-sm hover:border-slate-300 transition-colors"
            >
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 bg-brand-50 text-brand-700 text-xs rounded-full font-medium">
                    {ACTION_LABELS[log.actionType] || log.actionType}
                  </span>
                  <span className="font-medium text-slate-700">{log.auditorName}</span>
                  <span className="text-xs text-slate-400">({log.auditorRole})</span>
                </div>
                <span className="text-xs text-slate-400">{formatDate(log.createdAt)}</span>
              </div>
              <div className="text-slate-600">
                {log.targetType && (
                  <span className="text-xs text-slate-500 mr-2">
                    [{log.targetType}] {log.targetName || log.targetId}
                  </span>
                )}
                <span className="text-xs text-slate-400">사유: {log.reason}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
